import React, { useState, useCallback } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import Card from "components/card";
import { MdSearch, MdClose, MdDelete, MdVisibility, MdCheckCircle, MdCancel, MdRestoreFromTrash } from "react-icons/md";
import { getUserList, getUserDetail, toggleUserStatus, deleteUser } from "api/adapters/admin/admin";

// ─── Helpers ──────────────────────────────────────────────────────────────
function debounce(fn, delay) {
  let t;
  return (...args) => { clearTimeout(t); t = setTimeout(() => fn(...args), delay); };
}

function Avatar({ name, email, url }) {
  if (url) return <img src={url} alt={name} className="h-9 w-9 rounded-full object-cover" />;
  // Prefer first letter of email, fall back to display name initials
  const emailInitial = email ? email.charAt(0).toUpperCase() : null;
  const nameInitials = name?.trim().split(/\s+/).map((w) => w[0]).join("").slice(0, 2).toUpperCase() || "?";
  const display = emailInitial || nameInitials;
  return (
    <div className="flex h-9 w-9 items-center justify-center rounded-full bg-orange-500 text-xs font-bold text-white">
      {display}
    </div>
  );
}

function Badge({ active }) {
  return (
    <span className={`inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 text-xs font-semibold ${active ? "bg-green-100 text-green-700" : "bg-red-100 text-red-600"}`}>
      <span className={`h-1.5 w-1.5 rounded-full ${active ? "bg-green-500" : "bg-red-500"}`} />
      {active ? "Active" : "Inactive"}
    </span>
  );
}

function DeletedBadge() {
  return (
    <span className="inline-flex items-center gap-1 rounded-full bg-gray-100 px-2.5 py-0.5 text-xs font-semibold text-gray-500">
      <span className="h-1.5 w-1.5 rounded-full bg-gray-400" />
      Deleted
    </span>
  );
}

// ─── User Detail Modal ─────────────────────────────────────────────────────
function UserDetailModal({ userId, onClose }) {
  const { data, isLoading } = useQuery({
    queryKey: ["userDetail", userId],
    queryFn: () => getUserDetail(userId),
    enabled: !!userId,
  });

  const user = data?.data;
  const displayName = user?.sUsername || user?.sEmail?.split("@")[0] || "—";

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
      <div className="relative w-full max-w-md rounded-2xl bg-white dark:bg-navy-800 shadow-2xl">
        <button onClick={onClose} className="absolute right-4 top-4 text-gray-400 hover:text-gray-600">
          <MdClose className="h-5 w-5" />
        </button>
        <div className="p-6">
          <h3 className="mb-5 text-lg font-bold text-navy-700 dark:text-white">User Profile</h3>
          {isLoading ? (
            <div className="flex items-center justify-center py-10">
              <div className="h-8 w-8 animate-spin rounded-full border-4 border-orange-500 border-t-transparent" />
            </div>
          ) : user ? (
            <div className="flex flex-col gap-4">
              <div className="flex items-center gap-4">
                <Avatar name={displayName} email={user.sEmail} url={user.sProfilePicUrl} />
                <div>
                  <p className="font-bold text-navy-700 dark:text-white">{displayName}</p>
                  <p className="text-sm text-gray-500">{user.sEmail}</p>
                  {user.sBio && <p className="text-xs text-gray-400 mt-0.5 italic">{user.sBio}</p>}
                </div>
              </div>
              <div className="h-px bg-gray-100 dark:bg-white/10" />
              {[
                ["Username", user.sUsername || "—"],
                ["Kraken Connected", user.bIsKrakenConnected ? "Yes" : "No"],
                ["Profile Complete", user.bIsProfileComplete ? "Yes" : "No"],
                ["Status", <Badge key="s" active={user.isActive} />],
                ["Joined", user.dCreatedAt ? new Date(user.dCreatedAt).toLocaleDateString() : "—"],
              ].map(([label, val]) => (
                <div key={label} className="flex items-center justify-between">
                  <span className="text-sm text-gray-500">{label}</span>
                  <span className="text-sm font-semibold text-navy-700 dark:text-white">{val}</span>
                </div>
              ))}
              {user.oPassportStats?.dLastUpdated && (
                <>
                  <div className="h-px bg-gray-100 dark:bg-white/10" />
                  <p className="text-xs font-bold uppercase tracking-wider text-orange-500">Passport Stats</p>
                  {[
                    ["ROI", user.oPassportStats.sRoi ?? "—"],
                    ["Win Rate", user.oPassportStats.nWinRate != null ? `${user.oPassportStats.nWinRate}%` : "—"],
                    ["Total Trades", user.oPassportStats.nTotalTrades ?? "—"],
                    ["Best Asset", user.oPassportStats.sBestAsset ?? "—"],
                    ["Last Updated", new Date(user.oPassportStats.dLastUpdated).toLocaleDateString()],
                  ].map(([label, val]) => (
                    <div key={label} className="flex items-center justify-between">
                      <span className="text-sm text-gray-500">{label}</span>
                      <span className="text-sm font-semibold text-navy-700 dark:text-white">{val}</span>
                    </div>
                  ))}
                </>
              )}
            </div>
          ) : (
            <p className="text-sm text-red-500">User not found.</p>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── Confirm Dialog ────────────────────────────────────────────────────────
function ConfirmDialog({ message, onConfirm, onCancel, loading, error, isRestore }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
      <div className="w-full max-w-sm rounded-2xl bg-white dark:bg-navy-800 p-6 shadow-2xl">
        <p className="mb-6 text-center text-sm font-medium text-navy-700 dark:text-white">{message}</p>
        {error && (
          <p className="mb-4 rounded-lg bg-red-50 px-4 py-2.5 text-sm font-medium text-red-600 dark:bg-red-500/10 dark:text-red-400">
            {error}
          </p>
        )}
        <div className="flex gap-3">
          <button onClick={onCancel} className="flex-1 rounded-xl border border-gray-200 py-2.5 text-sm font-semibold text-gray-600 hover:bg-gray-50">
            Cancel
          </button>
          <button onClick={onConfirm} disabled={loading} className={`flex-1 rounded-xl py-2.5 text-sm font-semibold text-white disabled:opacity-60 ${isRestore ? "bg-green-500 hover:bg-green-600" : "bg-red-500 hover:bg-red-600"}`}>
            {loading ? "Please wait..." : isRestore ? "Restore" : "Confirm"}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Main Component ────────────────────────────────────────────────────────
const UserManagement = () => {
  const queryClient = useQueryClient();
  const [sSearch, setSearch] = useState("");
  const [nPage, setPage] = useState(1);
  const [viewId, setViewId] = useState(null);
  const [confirm, setConfirm] = useState(null);
  const [confirmError, setConfirmError] = useState("");

  const debouncedSearch = useCallback(
    debounce((val) => { setSearch(val); setPage(1); }, 400),
    []
  );

  const { data, isLoading, isFetching } = useQuery({
    queryKey: ["userList", sSearch, nPage],
    queryFn: () => getUserList({ sSearch, nPage, nLimit: 10 }),
    keepPreviousData: true,
  });

  const list = data?.data;
  const users = list?.aUsers ?? [];
  const nTotalPages = list?.nTotalPages ?? 1;

  const invalidate = () => queryClient.invalidateQueries({ queryKey: ["userList"] });

  const { mutate: doToggle, isPending: toggling } = useMutation({
    mutationFn: () => toggleUserStatus(confirm.id),
    onSuccess: () => { invalidate(); setConfirm(null); setConfirmError(""); },
    onError: (err) => setConfirmError(err.message || "Failed to update user status."),
  });

  const { mutate: doDelete, isPending: deleting } = useMutation({
    mutationFn: () => deleteUser(confirm.id),
    onSuccess: () => { invalidate(); setConfirm(null); setConfirmError(""); },
    onError: (err) => setConfirmError(err.message || "Failed to delete user."),
  });

  const handleConfirm = () => {
    if (confirm?.type === "delete") doDelete();
    else doToggle();
  };

  return (
    <div className="flex flex-col gap-5">
      <Card extra="p-5">
        {/* Header */}
        <div className="mb-5 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div>
            {/* <h2 className="text-xl font-bold text-navy-700 dark:text-white">User Management</h2>
            <p className="text-sm text-gray-500">
              {list ? `${list.nTotal} total users` : "Loading..."}
            </p> */}
          </div>
          <div className="relative w-full sm:w-64">
            <MdSearch className="absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              placeholder="Search by name or email..."
              onChange={(e) => debouncedSearch(e.target.value)}
              className="h-10 w-full rounded-xl border border-gray-200 bg-white pl-9 pr-3 text-sm outline-none focus:border-orange-500 focus:ring-1 focus:ring-orange-200 dark:border-white/10 dark:bg-navy-800 dark:text-white dark:focus:border-orange-400"
            />
          </div>
        </div>

        {/* Table */}
        <div className="overflow-x-auto">
          <table className="w-full min-w-[640px] text-left text-sm">
            <thead>
              <tr className="border-b-2 border-orange-100 dark:border-white/10">
                {["User", "Email", "Kraken", "Status", "Joined", "Actions"].map((h) => (
                  <th key={h} className="pb-3 pr-4 text-xs font-bold uppercase tracking-wider text-orange-500 dark:text-orange-400">
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {isLoading ? (
                Array.from({ length: 5 }).map((_, i) => (
                  <tr key={i} className="border-b border-gray-50 dark:border-white/5">
                    {Array.from({ length: 6 }).map((__, j) => (
                      <td key={j} className="py-3 pr-4">
                        <div className="h-4 animate-pulse rounded bg-gray-100 dark:bg-navy-700" />
                      </td>
                    ))}
                  </tr>
                ))
              ) : users.length === 0 ? (
                <tr>
                  <td colSpan={6} className="py-12 text-center text-sm text-gray-400">
                    No users found.
                  </td>
                </tr>
              ) : (
                users.map((u) => {
                  const displayName = u.sUsername || u.sEmail?.split("@")[0] || "—";
                  return (
                    <tr key={u._id} className={`border-b border-gray-50 transition-all hover:bg-orange-50 dark:border-white/5 dark:hover:bg-navy-700/50 ${isFetching ? "opacity-60" : ""} ${u.isDeleted ? "bg-gray-50 dark:bg-navy-900/40" : ""}`}>
                      <td className="py-3 pr-4">
                        <div className="flex items-center gap-2">
                          <Avatar name={displayName} email={u.sEmail} url={u.sProfilePicUrl} />
                          <span className={`font-semibold ${u.isDeleted ? "text-gray-400 line-through dark:text-gray-500" : "text-navy-700 dark:text-orange-400"}`}>{displayName}</span>
                        </div>
                      </td>
                      <td className={`py-3 pr-4 ${u.isDeleted ? "text-gray-400 dark:text-gray-500" : "text-navy-700 dark:text-white"}`}>{u.sEmail}</td>
                      <td className="py-3 pr-4">
                        <span className={`text-xs font-semibold ${u.bIsKrakenConnected ? "text-green-600 dark:text-green-400" : "text-gray-500"}`}>
                          {u.bIsKrakenConnected ? "Connected" : "—"}
                        </span>
                      </td>
                      <td className="py-3 pr-4">
                        {u.isDeleted ? <DeletedBadge /> : <Badge active={u.isActive} />}
                      </td>
                      <td className="py-3 pr-4 text-navy-700 dark:text-white">
                        {u.dCreatedAt ? new Date(u.dCreatedAt).toLocaleDateString() : "—"}
                      </td>
                      <td className="py-3">
                        <div className="flex items-center gap-1">
                          <button onClick={() => setViewId(u._id)} title="View"
                            className="rounded-lg p-1.5 text-orange-500 transition-all hover:bg-orange-100 hover:text-orange-600 dark:text-orange-400 dark:hover:bg-orange-500/20">
                            <MdVisibility className="h-4 w-4" />
                          </button>
                          {u.isDeleted ? (
                            <span className="rounded-lg p-1.5 w-[28px] h-[28px] inline-block" />
                          ) : (
                            <button
                              onClick={() => setConfirm({ type: "status", id: u._id, label: u.isActive ? "deactivate" : "activate", name: displayName })}
                              title={u.isActive ? "Deactivate" : "Activate"}
                              className="rounded-lg p-1.5 text-blue-500 transition-all hover:bg-blue-100 hover:text-blue-600 dark:text-blue-400 dark:hover:bg-blue-500/20">
                              {u.isActive ? <MdCancel className="h-4 w-4" /> : <MdCheckCircle className="h-4 w-4" />}
                            </button>
                          )}
                          <button
                            onClick={() => setConfirm({ type: "delete", id: u._id, name: displayName, isDeleted: u.isDeleted })}
                            title={u.isDeleted ? "Restore" : "Delete"}
                            className={`rounded-lg p-1.5 transition-all ${u.isDeleted ? "text-green-600 hover:bg-green-100 hover:text-green-700 dark:text-green-400 dark:hover:bg-green-500/20" : "text-red-500 hover:bg-red-100 hover:text-red-600 dark:text-red-400 dark:hover:bg-red-500/20"}`}>
                            {u.isDeleted ? <MdRestoreFromTrash className="h-4 w-4" /> : <MdDelete className="h-4 w-4" />}
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {nTotalPages > 1 && (
          <div className="mt-5 flex items-center justify-between">
            <p className="text-xs text-gray-500">Page {nPage} of {nTotalPages}</p>
            <div className="flex gap-2">
              <button onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={nPage === 1}
                className="rounded-lg border border-orange-200 px-3 py-1.5 text-xs font-medium text-orange-600 hover:bg-orange-50 disabled:opacity-40 dark:border-white/10 dark:text-gray-400 dark:hover:bg-navy-700">
                Previous
              </button>
              <button onClick={() => setPage((p) => Math.min(nTotalPages, p + 1))} disabled={nPage === nTotalPages}
                className="rounded-lg border border-orange-200 px-3 py-1.5 text-xs font-medium text-orange-600 hover:bg-orange-50 disabled:opacity-40 dark:border-white/10 dark:text-gray-400 dark:hover:bg-navy-700">
                Next
              </button>
            </div>
          </div>
        )}
      </Card>

      {viewId && <UserDetailModal userId={viewId} onClose={() => setViewId(null)} />}
      {confirm && (
        <ConfirmDialog
          message={confirm.type === "delete"
            ? confirm.isDeleted
              ? `Restore "${confirm.name}"? They will be able to log in again.`
              : `Are you sure you want to delete "${confirm.name}"?`
            : `Are you sure you want to ${confirm.label} "${confirm.name}"?`}
          onConfirm={handleConfirm}
          onCancel={() => { setConfirm(null); setConfirmError(""); }}
          loading={toggling || deleting}
          error={confirmError}
          isRestore={confirm.type === "delete" && confirm.isDeleted}
        />
      )}
    </div>
  );
};

export default UserManagement;
